import { defineStore } from 'pinia';
import * as _ from 'lodash';
import { useRoute } from 'vue-router';
import {
  Challenge,
  ChallengeId,
  ChallengeTypeEnum,
  RestrictedChallenge,
  ValueBasedChallengeParams,
} from '../types/challenge';
import * as ChallengesApi from '../api/challengesApi';
import Segment from '../Segment';
import { dateDifferenceInDays } from '../utils/date';
import { hasParticipantFailedPercentBasedChallenge, hasParticipantFailedValueBasedChallenge } from '../utils/participant';
import { Participant, ParticipantChallengeData } from '../types/participant';
import { EmptyObject } from '../types/general';
import { availableColors, availableEmojis } from '../consts/challenge';
import { updateParticipantChallengeGoal } from '../api/challengesApi';

interface State {
  challenge: Challenge | RestrictedChallenge | EmptyObject;
  participantInitialData: ParticipantChallengeData | EmptyObject;
  challengeId?: string,
  participantId?: string,
  loading: boolean;
}

export const maxParticipantsInList = 11;
const maxIconsInParticipantsStack = 4;
const middleOfMaxParticipantsInList = Math.floor(maxParticipantsInList / 2);
export const useChallengeStore = defineStore('challenge', {
  state: (): State => ({
    challengeId: undefined,
    challenge: {},
    participantInitialData: {},
    loading: false,
    participantId: undefined,
  }),
  getters: {
    participant(state): Participant | undefined {
      return state.challenge?.participants && state.challengeId
        ? _.find(state.challenge.participants, { participantId: state.participantId })
        : undefined;
    },
    orderedParticipants(state): Participant[] {
      if (!state.challenge?.participants) {
        return [];
      }
      return _.chain(state.challenge.participants)
        .map(p => ({ ...p, isMe: p.participantId === state.participantId }))
        .orderBy(['challengeData.expenseAmount', 'isMe'], ['asc', 'desc'])
        .value();
    },
    indexOfMe(): number {
      return _.findIndex(this.orderedParticipants, p => p.isMe === true);
    },

    isInBottomSpenders(): boolean {
      return this.indexOfMe - middleOfMaxParticipantsInList < 0;
    },
    isInTopSpenders(): boolean {
      return this.indexOfMe >= this.orderedParticipants.length - middleOfMaxParticipantsInList;
    },
    firstInList(): number {
      if (this.isInBottomSpenders) {
        return 0;
      }
      if (this.isInTopSpenders) {
        return this.orderedParticipants.length - maxParticipantsInList;
      }
      const startIndex = this.indexOfMe - middleOfMaxParticipantsInList;
      return (startIndex === 1) ? 0 : startIndex;
    },
    lastInList(): number {
      if (this.isInTopSpenders) {
        return this.orderedParticipants.length - 1;
      }
      if (this.isInBottomSpenders) {
        return maxParticipantsInList - 1;
      }
      const endIndex = this.indexOfMe + middleOfMaxParticipantsInList;
      return (endIndex === this.orderedParticipants.length - 2 ? this.orderedParticipants.length - 1 : endIndex);
    },
    orderedParticipantsForDisplay(): Participant[] {
      if (this.orderedParticipants.length <= maxParticipantsInList) {
        return this.orderedParticipants;
      }
      return this.orderedParticipants.slice(this.firstInList, this.lastInList + 1);
    },
    numberOfParticipantsWhoSpentLess(): number {
      return this.firstInList;
    },
    numberOfParticipantsWhoSpentMore(): number {
      return this.orderedParticipants.length - this.lastInList - 1;
    },
    numberOfParticipantsWhoExceededChallengeGoal(): number {
      return _.filter(this.orderedParticipants, (p => {
        if (this.isPercentBasedChallenge) {
          return hasParticipantFailedPercentBasedChallenge(p);
        } if (this.isValueBasedChallenge) {
          return hasParticipantFailedValueBasedChallenge(p, this.valueBasedChallengeGoalAmount);
        }
        return false;
      })).length;
    },
    numberOfParticipantsWhoSpentLessThanChallengeGoal(): number {
      return this.orderedParticipants.length - this.numberOfParticipantsWhoExceededChallengeGoal;
    },
    failedStackParticipants(): Participant[] {
      return this.numberOfParticipantsWhoExceededChallengeGoal > 0
        ? this.orderedParticipantsForDisplay.slice(-(Math.min(
          this.numberOfParticipantsWhoExceededChallengeGoal,
          maxIconsInParticipantsStack,
        )))
        : [];
    },
    successStackParticipants(): Participant[] {
      return this.numberOfParticipantsWhoSpentLessThanChallengeGoal > 0
        ? _.reverse(this.orderedParticipantsForDisplay.slice(
          0,
          (Math.min(this.numberOfParticipantsWhoSpentLessThanChallengeGoal, maxIconsInParticipantsStack)),
        ))
        : [];
    },
    isInChallenge(state): boolean {
      return !!(state.participantId && _.find(this.orderedParticipants, p => p.participantId === state.participantId));
    },
    timeUntilChallengeStart(state): number {
      return dateDifferenceInDays(new Date(), state.challenge.startDate);
    },
    hasChallengeStarted(state): boolean {
      return state.challenge?.startDate ? dateDifferenceInDays(state.challenge?.startDate, new Date()) > 0 : false;
    },
    hasChallengeEnded(state): boolean {
      return state.challenge?.endDate ? dateDifferenceInDays(state.challenge?.endDate, new Date()) > 1 : false;
    },
    amountSpent(): number {
      return this.participant?.challengeData?.expenseAmount || 0;
    },
    hasExceededChallengeGoal(): boolean {
      return this.amountSpent > this.challengeGoal;
    },
    amountLeftOver(): number {
      return (this.challengeGoal || 0) - this.amountSpent;
    },
    hasMaxParticipantsInListExceeded(): boolean {
      return this.orderedParticipants.length > maxParticipantsInList;
    },
    isPercentBasedChallenge(state: State): boolean {
      return state.challenge.type === ChallengeTypeEnum.Values.percentBased;
    },
    isValueBasedChallenge(state: State): boolean {
      return state.challenge.type === ChallengeTypeEnum.Values.valueBased;
    },
    isPersonalValueBasedChallenge(state: State): boolean {
      return state.challenge.type === ChallengeTypeEnum.Values.personalValueBased;
    },
    valueBasedChallengeGoalAmount(state: State): number {
      return ValueBasedChallengeParams.parse(state.challenge.challengeParams)?.amount ?? 0;
    },
    challengeGoal(state: State): number {
      if (_.isEmpty(state.challenge)) {
        return 0;
      }
      const participantChallengeGoal = this.participant
        ? ParticipantChallengeData.parse(this.participant.challengeData)?.challengeGoal
        : ParticipantChallengeData.parse(state.participantInitialData)?.challengeGoal;

      if (participantChallengeGoal) {
        return participantChallengeGoal;
      }
      // support old value based participants
      if (state.challenge.type === ChallengeTypeEnum.Values.valueBased) {
        return this.valueBasedChallengeGoalAmount;
      }
      return 0;
    },
    firstName(): string | undefined {
      return this.participant?.firstName;
    },
    expenseCategory(state: State): string {
      const categoryName = state.challenge.challengeParams.expenseCategory ?? '';
      return categoryName === 'כלכלה' ? 'סופר' : categoryName;
    },
  },
  actions: {
    async initForVisitor(challengeId: ChallengeId) {
      this.challengeId = challengeId;
      this.challenge = await ChallengesApi.getRestrictedChallengeById(this.challengeId);
    },
    async init(challengeId: ChallengeId) {
      this.challengeId = challengeId;
      await this.fetchChallengeAndParticipant();
    },
    async fetchChallengeAndParticipant() {
      await this.fetchChallenge();
      await this.fetchParticipantId();
      if (!this.participantId) {
        await this.fetchNonParticipantChallengeData();
      }
    },
    async removeParticipantFromChallenge() {
      Segment.trackUserInteraction(
        'remove_from_challenge_clicked',
        {
          hasChallengeStarted: this.hasChallengeStarted,
          challengeId: this.challengeId,
          memberId: this.participant?.memberId,
        },
      );
      await ChallengesApi.removeParticipantFromChallenge(this.challengeId, this.participantId);
    },
    async fetchChallenge() {
      this.challenge = await ChallengesApi.getChallengeById(this.challengeId);
    },
    async fetchNonParticipantChallengeData() {
      this.participantInitialData = await ChallengesApi.getNonParticipantChallengeData(this.challengeId);
    },
    async fetchParticipantId() {
      const participant = await ChallengesApi.getParticipantForChallenge(this.challengeId);
      if (participant) {
        this.participantId = participant.participantId;
      }
    },
    async addParticipant(personalGoal?: number) {
      try {
        this.loading = true;
        const route = useRoute();
        const utmParams = _.pickBy(route.query, (value, key) => _.startsWith(key, 'utm_'));

        Segment.trackUserInteraction(
          'join_challenge_click',
          {
            hasChallengeStarted: this.hasChallengeStarted,
            challengeId: this.challengeId,
            personalGoal,
          },
        );
        const emoji = _.sample(availableEmojis);
        const color = _.sample(availableColors);
        const challengeData = { emoji, color, challengeGoal: personalGoal };
        this.participantId = await ChallengesApi.addParticipantToChallenge(this.challengeId, challengeData, utmParams);
        await this.fetchChallenge();
        Segment.trackUserInteraction(
          'join_challenge_success',
          {
            hasChallengeStarted: this.hasChallengeStarted,
            challengeId: this.challengeId,
            memberId: this.participant?.memberId,
          },
        );
        Segment.trackUserGotToPixel('ChallengeFinishedRegistration', { challengeId: this.challengeId, memberId: this.participant?.memberId });
      } catch (e) {
        Segment.trackUserInteraction(
          'join_challenge_failed',
          { hasChallengeStarted: this.hasChallengeStarted, challengeId: this.challengeId },
        );
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async updateParticipantPersonalGoal(personalGoal: number) {
      try {
        this.loading = true;
        Segment.trackUserInteraction(
          'update_personal_goal_click',
          {
            hasChallengeStarted: this.hasChallengeStarted,
            challengeId: this.challengeId,
            personalGoal,
            memberId: this.participant?.memberId,
          },
        );
        if (!this.participantId) {
          throw new Error('participantId is undefined');
        }
        await updateParticipantChallengeGoal(this.participantId, personalGoal);
        await this.fetchChallenge();

        Segment.trackUserInteraction(
          'update_personal_goal_success',
          {
            hasChallengeStarted: this.hasChallengeStarted,
            challengeId: this.challengeId,
            memberId: this.participant?.memberId,
          },
        );
      } catch (e) {
        Segment.trackUserInteraction(
          'update_personal_goal_failed',
          {
            hasChallengeStarted: this.hasChallengeStarted,
            challengeId: this.challengeId,
            memberId: this.participant?.memberId,
          },
        );
        throw e;
      } finally {
        this.loading = false;
      }
    },

  },
});
